/**
 * API fetch wrapper for Filmpl
 *
 * Centralized fetch utility with:
 * - Base URL configuration
 * - Authorization header injection
 * - Consistent error handling
 * - AbortSignal support
 */

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';

export interface ApiFetchOptions {
  token?: string;
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  signal?: AbortSignal;
  headers?: Record<string, string>;
}

export class ApiError extends Error {
  status: number;
  code?: string;

  constructor(message: string, status: number, code?: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
  }
}

/**
 * Fetch from the Filmpl API
 *
 * @param path - API path (e.g., '/watchlists' or '/ratings')
 * @param options - Fetch options
 * @returns Parsed JSON response
 * @throws ApiError on non-2xx responses
 */
export async function apiFetch<T = unknown>(
  path: string,
  options: ApiFetchOptions = {}
): Promise<T> {
  const { token, method = 'GET', body, signal, headers = {} } = options;

  const url = `${API_BASE_URL}${path}`;

  const requestHeaders: Record<string, string> = {
    ...headers,
  };

  if (token) {
    requestHeaders['Authorization'] = `Bearer ${token}`;
  }

  if (body !== undefined) {
    requestHeaders['Content-Type'] = 'application/json';
  }

  const response = await fetch(url, {
    method,
    headers: requestHeaders,
    body: body !== undefined ? JSON.stringify(body) : undefined,
    signal,
    cache: 'no-store', // Defense-in-depth: prevent stale data for live polling
  });

  // Handle non-2xx responses
  if (!response.ok) {
    let errorMessage = `Request failed with status ${response.status}`;
    let errorCode: string | undefined;

    try {
      const errorData = await response.json();
      if (errorData.error) {
        errorMessage = errorData.error;
      }
      if (errorData.code) {
        errorCode = errorData.code;
      }
    } catch {
      // Response wasn't JSON, use default message
    }

    // Dispatch session-expired event for 401 Unauthorized
    // SessionExpiredHandler listens for this and triggers sign out + toast
    if (response.status === 401) {
      window.dispatchEvent(new CustomEvent('auth:session-expired'));
    }

    // Dispatch account-closed event for 403 ACCOUNT_SUSPENDED
    if (response.status === 403 && errorCode === 'ACCOUNT_SUSPENDED') {
      window.dispatchEvent(new CustomEvent('auth:account-closed'));
    }

    throw new ApiError(errorMessage, response.status, errorCode);
  }

  // Handle empty responses (204 No Content or empty body on 200)
  if (response.status === 204) {
    return undefined as T;
  }

  // Robust handling: some endpoints may return empty body on success
  const text = await response.text();
  if (text.trim() === '') {
    return undefined as T;
  }

  return JSON.parse(text) as T;
}
